import * as React from "react"
import type { Interval, IntervalOrigin } from "@digits-graphql/frontend/graphql-bearer"
import { isAllTimeUrlPath } from "@digits-shared/helpers/allTimeHelpers"
import dateTimeHelper from "@digits-shared/helpers/dateTimeHelper"
import useRouter from "@digits-shared/hooks/useRouter"
import { createAllTimeOrigin } from "src/shared/allTime/allTimeHelpers"
import { useTransactionTimestamps } from "src/shared/hooks/useTransactionTimestamps"

interface AllTimeOriginResult {
  allTimeOrigin: IntervalOrigin | undefined
  /** Checks if the current URL path matches the all-time pattern */
  isAllTimeUrl: boolean
  /** Checks if a specific origin matches the computed all-time origin */
  isAllTimeOrigin: (origin: IntervalOrigin) => boolean
  loading: boolean
}

/**
 * Hook to determine if All Time date range feature is enabled.
 * Enabled for all users.
 */
export function useAllTimeEnabled(): boolean {
  return true
}

/**
 * Hook to compute and track all-time origin state.
 * Uses the centralized all-time module for helpers.
 */
export function useAllTimeOrigin(interval: Interval, skip?: boolean): AllTimeOriginResult {
  const { firstTransactionTimestamp, maxNavigableTimestamp, loading } =
    useTransactionTimestamps(skip)
  const { location } = useRouter()

  const allTimeOrigin = React.useMemo<IntervalOrigin | undefined>(
    () => createAllTimeOrigin(interval, firstTransactionTimestamp, maxNavigableTimestamp),
    [firstTransactionTimestamp, interval, maxNavigableTimestamp]
  )

  const isAllTimeUrl = React.useMemo(() => isAllTimeUrlPath(location.pathname), [location.pathname])

  const isAllTimeOrigin = React.useCallback(
    (origin: IntervalOrigin): boolean => {
      if (!allTimeOrigin) return false
      return dateTimeHelper.areIntervalOriginsEqual(origin, allTimeOrigin)
    },
    [allTimeOrigin]
  )

  return {
    allTimeOrigin,
    isAllTimeUrl,
    isAllTimeOrigin,
    loading,
  }
}
